/* Auth + settings. */
import { ApiError } from "../provider";
import type * as T from "../types";
import { isBusinessCategory, isCountryCode } from "../types";
import { siDefaults, validCrFor, validVatFor } from "../../domain/country";
import { crMessage, vatMessage } from "./countryRules";
import { E, has, isInt, isNum, str, type Ctx } from "./ctx";
import { PLATFORM } from "./state";

const ROLES: T.Role[] = ["owner", "hr", "accountant", "employee", "admin"];

export function authOps(c: Ctx) {
  const pub = (u: T.User) => c.publicUser(u);
  const respond = (u: T.User): T.LoginResponse => {
    const token = c.issueToken(u);
    c.token = token;
    return { token, user: pub(u) };
  };
  return {
    async login(req: T.LoginRequest): Promise<T.LoginResponse> {
      const email = str(req?.email).trim().toLowerCase();
      const password = str(req?.password);
      const fields: Record<string, string> = {};
      if (!email) fields.email = "required";
      if (!password) fields.password = "required";
      if (Object.keys(fields).length) throw E.validation(fields);
      const u = c.state.users.find((x) => x.email.toLowerCase() === email);
      if (!u || password !== (c.state.passwords[u.id] ?? c.state.demoPassword)) throw E.unauthorized("invalid email or password");
      if (c.state.unverified.has(u.id)) throw new ApiError(403, "email_not_verified", "email address not verified — check your inbox or resend the link");
      return respond(u);
    },

    async switchRole(role: T.Role): Promise<T.LoginResponse> {
      const me = c.principal("authenticated");
      if (!ROLES.includes(role)) throw E.field("role", "must be one of owner, hr, accountant, employee, admin");
      let u: T.User | undefined;
      if (role === "admin") u = c.state.users.find((x) => x.role === "admin" && x.tenantId === PLATFORM);
      else if (me.tenantId === PLATFORM) u = c.state.users.find((x) => x.role === role && x.tenantId !== PLATFORM);
      else u = c.state.users.find((x) => x.role === role && x.tenantId === me.tenantId);
      if (!u) throw E.notFound("no user with that role");
      return respond(u);
    },

    async me(): Promise<T.User> {
      return pub(c.principal("authenticated"));
    },

    async clock(): Promise<T.Clock> {
      c.principal("authenticated");
      return { today: c.today, now: c.now };
    },

    async logout(): Promise<void> {
      c.principal("authenticated");
      if (c.token) c.state.revoked.add(c.token);
      c.token = null;
    },

    async getSettings(): Promise<T.Settings> {
      c.principal("settings.read");
      return c.state.settings;
    },

    async updateSettings(patch: Partial<Omit<T.Settings, "tenant">> & { tenant?: Partial<T.Tenant> }): Promise<T.Settings> {
      const u = c.principal("settings.write");
      if (patch == null || typeof patch !== "object") throw E.badRequest("JSON object expected");
      const st = structuredClone(c.state.settings);
      const fe: Record<string, string> = {};
      const sub = (k: string): Record<string, unknown> | null => {
        const v = (patch as Record<string, unknown>)[k];
        if (v == null) return null;
        if (typeof v !== "object" || Array.isArray(v)) throw E.field(k, "must be an object");
        return v as Record<string, unknown>;
      };
      const numIn = (m: Record<string, unknown>, k: string, path: string, min: number, max: number, minExclusive: boolean, set: (n: number) => void) => {
        if (!has(m, k)) return;
        const f = m[k];
        if (!isNum(f) || f > max || f < min || (minExclusive && f <= min)) {
          fe[path] = minExclusive ? "must be > 0" : `must be between ${min} and ${max}`;
          return;
        }
        set(f);
      };
      const intIn = (m: Record<string, unknown>, k: string, path: string, min: number, max: number, set: (n: number) => void) => {
        if (!has(m, k)) return;
        const n = m[k];
        if (!isInt(n) || n < min || n > max) { fe[path] = `must be an integer between ${min} and ${max}`; return; }
        set(n);
      };
      const t = sub("tenant");
      if (t) {
        if (has(t, "name")) { const v = str(t.name).trim(); if (!v) fe["tenant.name"] = "required"; else st.tenant.name = v; }
        if (has(t, "nameAr")) st.tenant.nameAr = str(t.nameAr);
        if (has(t, "city")) st.tenant.city = str(t.city);
        // country first: CR / VAT below are checked against the (new) country's formats (Go putSettings)
        if (has(t, "country")) {
          const v = str(t.country);
          if (!isCountryCode(v)) fe["tenant.country"] = "unknown country";
          else if (v !== st.tenant.country) { st.tenant.country = v; st.gosi = siDefaults(v); }
        }
        const cc = st.tenant.country ?? "SA";
        if (has(t, "cr")) { const v = str(t.cr); if (!validCrFor(v, cc)) fe["tenant.cr"] = crMessage(cc); else st.tenant.cr = v; }
        if (has(t, "vat")) { const v = str(t.vat); if ((cc === "SA" && !/^\d{15}$/.test(v)) || !validVatFor(v, cc)) fe["tenant.vat"] = vatMessage(cc); else st.tenant.vat = v; }
        if ("category" in t) { const v = typeof t.category === "string" ? t.category : ""; if (v && !isBusinessCategory(v)) fe["tenant.category"] = "unknown category"; else st.tenant.category = v as T.BusinessCategory | ""; }
      }
      const g = sub("gosi");
      if (g) {
        for (const k of ["saEmpAnn", "saEmpSaned", "saErAnn", "saErSaned", "saErOH", "nsErOH", "nsEmp"] as const) numIn(g, k, "gosi." + k, 0, 100, false, (n) => { st.gosi[k] = n; });
        numIn(g, "cap", "gosi.cap", 0, 1e9, true, (n) => { st.gosi.cap = n; });
      }
      const al = sub("allowances");
      if (al) for (const k of ["housing", "transport"] as const) numIn(al, k, "allowances." + k, 0, 100, false, (n) => { st.allowances[k] = n; });
      intIn(patch as Record<string, unknown>, "payDay", "payDay", 1, 28, (n) => { st.payDay = n; });
      const at = sub("attendance");
      if (at) {
        for (const k of ["shiftStart", "shiftEnd"] as const) {
          if (!has(at, k)) continue;
          const v = str(at[k]);
          if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(v)) fe["attendance." + k] = "must be HH:mm";
          else st.attendance[k] = v;
        }
        intIn(at, "graceMinutes", "attendance.graceMinutes", 0, 120, (n) => { st.attendance.graceMinutes = n; });
        intIn(at, "dedupeSeconds", "attendance.dedupeSeconds", 0, 3600, (n) => { st.attendance.dedupeSeconds = n; });
        intIn(at, "overtimeAfterMinutes", "attendance.overtimeAfterMinutes", 0, 1440, (n) => { st.attendance.overtimeAfterMinutes = n; });
        if (has(at, "weekendDays")) {
          const arr = at.weekendDays;
          const days: number[] = [];
          let valid = Array.isArray(arr);
          if (Array.isArray(arr)) {
            for (const x of arr) {
              if (!isInt(x) || x < 0 || x > 6) { valid = false; break; }
              if (!days.includes(x)) days.push(x);
            }
          }
          if (!valid) fe["attendance.weekendDays"] = "must be a list of weekdays 0–6";
          else st.attendance.weekendDays = days;
        }
        if (!fe["attendance.shiftStart"] && !fe["attendance.shiftEnd"] && st.attendance.shiftEnd <= st.attendance.shiftStart) fe["attendance.shiftEnd"] = "must be after shiftStart";
      }
      if (Object.keys(fe).length) throw E.validation(fe);
      c.state.settings = st;
      const ts = c.state.tenants.find((x) => x.id === c.tid);
      if (ts) { ts.name = st.tenant.name; ts.cr = st.tenant.cr; ts.city = st.tenant.city; ts.category = st.tenant.category; ts.country = st.tenant.country; }
      c.audit(u, "Updated settings", "Settings", c.tid, Object.keys(patch).sort().join(", "));
      return st;
    },
  };
}
